import type {
  AdminAppealDetailDto,
  AdminAppealPriority,
  AdminAppealStatus,
  ListAdminAppealsResultDto,
  ResolveAppealBody,
} from '@carcheck/shared';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { apiClient } from './client';

/**
 * TanStack Query hooks for the admin appeals moderation surface
 * (AUT-130 / ADMIN-18), targeting the merged AUT-119 read DTOs +
 * AUT-105 actions:
 *
 *   - `GET  /admin/appeals`              (cursor-paginated)
 *   - `GET  /admin/appeals/:id`
 *   - `POST /admin/appeals/:id/review`   (review_start — empty body)
 *   - `POST /admin/appeals/:id/resolve`  (`ResolveAppealBody`)
 *
 * Query-key convention (ADMIN-10 guardrail / queryClient.ts):
 *   list   → `['admin', 'appeals', 'list', filters]`
 *   detail → `['admin', 'appeals', 'detail', id]`
 * Both mutations invalidate by the `['admin', 'appeals']` prefix on success.
 *
 * Mirrors `admin-reports.ts` / `admin-payouts.ts`. Both POSTs are plain —
 * AUT-138 removed per-action TOTP step-up; the actions are role +
 * permission + audit gated server-side.
 *
 * INVALIDATE-ONLY: both POSTs return the user-facing `AppealDto` shape
 * (different from `AdminAppealDetailDto`); the SPA does NOT cross-patch
 * the detail cache — it invalidates the prefix and lets TanStack refetch.
 *
 * Server state lives in TanStack; the auth/session permission set lives in
 * MobX. The two never duplicate a datum.
 */

const APPEALS_BASE = '/admin/appeals';

/** Stable query-key prefix; both mutations invalidate against this. */
export const ADMIN_APPEALS_KEY = ['admin', 'appeals'] as const;

/**
 * Filter / pagination params for the list query. Mirrors the backend
 * `ListAdminAppealsQuerySchema`:
 *   - `status`   — multi-select over the LIVE 3-state `ADMIN_APPEAL_STATUSES`
 *                  (CSV on the wire). `approved` / `rejected` are dead and
 *                  rejected by the api (architect lock #1).
 *   - `priority` — multi-select over the full 3-value `ADMIN_APPEAL_PRIORITIES`.
 *   - `orderId` / `customerId` / `inspectorId` — exact-match UUIDs.
 *   - `dateFrom` / `dateTo` — ISO date strings bounding `createdAt`.
 *   - `cursor` / `limit` — cursor pagination over `createdAt DESC`.
 * NO `reason` filter — the api does not filter on reason (the wire union
 * is published only so the SPA can render the reason chip).
 */
export interface AdminAppealsListParams {
  status?: AdminAppealStatus[];
  priority?: AdminAppealPriority[];
  orderId?: string;
  customerId?: string;
  inspectorId?: string;
  dateFrom?: string;
  dateTo?: string;
  cursor: string | null;
  limit: number;
}

/** Build the URLSearchParams a list fetch sends (omits empty values). */
const buildListParams = (params: AdminAppealsListParams): Record<string, string> => {
  const query: Record<string, string> = { limit: String(params.limit) };
  if (params.status && params.status.length > 0) query.status = params.status.join(',');
  if (params.priority && params.priority.length > 0) query.priority = params.priority.join(',');
  if (params.orderId && params.orderId.length > 0) query.orderId = params.orderId;
  if (params.customerId && params.customerId.length > 0) query.customerId = params.customerId;
  if (params.inspectorId && params.inspectorId.length > 0) query.inspectorId = params.inspectorId;
  if (params.dateFrom) query.dateFrom = params.dateFrom;
  if (params.dateTo) query.dateTo = params.dateTo;
  if (params.cursor) query.cursor = params.cursor;
  return query;
};

export const useAdminAppealsList = (
  params: AdminAppealsListParams,
): UseQueryResult<ListAdminAppealsResultDto, Error> =>
  useQuery({
    queryKey: [...ADMIN_APPEALS_KEY, 'list', params],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<ListAdminAppealsResultDto>(APPEALS_BASE, {
        params: buildListParams(params),
        signal,
      });
      return data;
    },
  });

export const useAdminAppealDetail = (
  id: string | null,
): UseQueryResult<AdminAppealDetailDto, Error> =>
  useQuery({
    queryKey: [...ADMIN_APPEALS_KEY, 'detail', id],
    enabled: id !== null,
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<AdminAppealDetailDto>(`${APPEALS_BASE}/${id}`, {
        signal,
      });
      return data;
    },
  });

/**
 * Low-level review-start mutation. Empty request body — the server
 * transitions `pending → under_review` (`startReview`).
 *
 * Returns the user-facing `AppealDto` (typed as `unknown` here — caller
 * invalidates the prefix; the SPA never reads the response body).
 */
export const postReviewStartAppeal = async (id: string): Promise<void> => {
  await apiClient.post<unknown>(`${APPEALS_BASE}/${id}/review`, {});
};

export interface ResolveAppealVariables {
  id: string;
  body: ResolveAppealBody;
}

/**
 * Low-level resolve mutation. `body` is the validated `ResolveAppealBody`
 * (mirrors backend `ResolveAppealSchema`). Terminal — records the
 * AppealOutcome + optional refund + optional inspector penalty, moves real
 * WebPay money for the refund outcomes. Role + permission + audit gated
 * server-side (AUT-138 removed per-action TOTP step-up).
 *
 * Returns the user-facing `AppealDto` envelope (typed `unknown`) — the
 * caller invalidates rather than cross-patching the open drawer's detail
 * shape.
 */
export const postResolveAppeal = async (variables: ResolveAppealVariables): Promise<void> => {
  await apiClient.post<unknown>(`${APPEALS_BASE}/${variables.id}/resolve`, variables.body);
};

/**
 * Cache-helper for the appeal action hooks. ONLY exposes `invalidate`
 * (no `setQueryData`): both mutations return the user-facing `AppealDto`
 * shape, NOT `AdminAppealDetailDto`, so a cross-patch would mismatch the
 * open drawer's shape. Refetch via invalidation is the source of truth —
 * and it re-syncs the appeal status (`pending → under_review → resolved`)
 * the footer gating reads.
 */
export const useAdminAppealsCache = (): {
  invalidate: () => Promise<void>;
} => {
  const queryClient = useQueryClient();
  return {
    invalidate: () => queryClient.invalidateQueries({ queryKey: ADMIN_APPEALS_KEY }),
  };
};
